import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Film, Loader2, Sparkles, Download, ExternalLink, ArrowLeft, Wand2, Volume2, Clock, CheckCircle2, AlertCircle, Play, User, Upload, X, ShieldCheck, Trash2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { EditProjectModal, DownloadButton } from "@/components/ShowoEditKit";

// Fliboe Showo — one-sentence-to-movie generator ("Cinely-Lite").
// Pipeline runs on the backend: Claude script → Nano Banana scenes →
// OpenAI TTS narration → FFmpeg Ken-Burns + concat → MP4 → optional publish.
// Admin-only for MVP. Poll pattern identical to Ampliboe to survive the
// Cloudflare 100s edge timeout.

const SHOWO_TOKEN_KEY = "fliboe_showo_token";
function bootstrap() {
  try {
    if (!localStorage.getItem(SHOWO_TOKEN_KEY) && localStorage.getItem("fliboe_token")) {
      localStorage.setItem(SHOWO_TOKEN_KEY, localStorage.getItem("fliboe_token"));
    }
  } catch (_) {}
}
if (typeof window !== "undefined") bootstrap();
const auth = () => { bootstrap(); const t = localStorage.getItem(SHOWO_TOKEN_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };

const STATUS_LABELS = {
  queued: "Queued",
  scripting: "Writing script…",
  imaging: "Painting scenes…",
  narrating: "Recording narration…",
  rendering: "Rendering movie…",
  publishing: "Uploading…",
  ready: "Ready",
  failed: "Failed",
};

export default function ShowoMake() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [genre, setGenre] = useState("drama");
  const [length, setLength] = useState("short");
  const [voice, setVoice] = useState("onyx");
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null); // {job_id, status, step, video_url, ...}
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);
  const pollRef = useRef(null);
  // Face-swap ("Star in your own movie")
  const [face, setFace] = useState(null); // {face_id, description, preview_url}
  const [faceBusy, setFaceBusy] = useState(false);
  const [faceErr, setFaceErr] = useState("");
  const [tcOpen, setTcOpen] = useState(false);
  const [tcAccepted, setTcAccepted] = useState(false);
  const fileRef = useRef(null);

  const isSignedIn = typeof window !== "undefined" && !!localStorage.getItem(SHOWO_TOKEN_KEY);

  useEffect(() => {
    api.get("/showo/make/config").then(r => setConfig(r.data)).catch(() => {});
    if (isSignedIn) refreshHistory();
    return () => { if (pollRef.current) { clearTimeout(pollRef.current); clearInterval(pollRef.current); } };
  }, [isSignedIn]);

  const refreshHistory = async () => {
    try {
      const r = await api.get("/showo/make/list/mine", { headers: auth() });
      setHistory(r.data?.jobs || []);
    } catch (_) { /* not signed in */ }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) { clearTimeout(pollRef.current); clearInterval(pollRef.current); pollRef.current = null; }
    // Fault-tolerant polling — a single Cloudflare blip, token refresh, or
    // backend restart should NOT kill the render UI. We accept up to
    // MAX_FAILS consecutive errors and back off between them. Only give up
    // after ~2 minutes of solid failures.
    let consecutiveFails = 0;
    const MAX_FAILS = 20;   // 20 × ~6s ≈ 2 min of down-time tolerated
    const BASE_MS = 4000;
    let currentDelay = BASE_MS;

    const tick = async () => {
      try {
        const r = await api.get(`/showo/make/${jobId}`, { headers: auth() });
        consecutiveFails = 0;
        currentDelay = BASE_MS;
        setActive(r.data);
        if (r.data.status === "ready" || r.data.status === "failed") {
          if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
          refreshHistory();
          return;
        }
        pollRef.current = setTimeout(tick, currentDelay);
      } catch (e) {
        // 401 → session died mid-render; surface a clear message and stop
        if (e?.response?.status === 401) {
          if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
          setErr("Your session ended — sign in again and open Your recent movies to grab the finished video.");
          return;
        }
        consecutiveFails += 1;
        if (consecutiveFails >= MAX_FAILS) {
          if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
          setErr("Lost connection to the render job — the movie is still cooking on the server. Check Your recent movies below in a minute.");
          // Refresh history in the background so the finished movie appears there
          setTimeout(refreshHistory, 15000);
          return;
        }
        // Exponential backoff, capped at 20s
        currentDelay = Math.min(currentDelay * 1.5, 20000);
        pollRef.current = setTimeout(tick, currentDelay);
      }
    };
    // Kick off — use setTimeout instead of setInterval so backoff is honored
    pollRef.current = setTimeout(tick, BASE_MS);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!isSignedIn) {
      navigate("/showo/login?next=" + encodeURIComponent("/showo/make"));
      return;
    }
    if (prompt.trim().length < 8) { setErr("Describe your movie in at least a full sentence."); return; }
    setBusy(true);
    try {
      const body = { prompt: prompt.trim(), genre, length, voice, publish };
      if (face?.face_id) { body.face_id = face.face_id; body.tc_accepted = true; }
      const r = await api.post("/showo/make", body, { headers: auth() });
      const jobId = r.data.job_id;
      setActive({ job_id: jobId, status: "queued", step: "Queued" });
      startPolling(jobId);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not start the render. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = () => {
    if (!tcAccepted) { setTcOpen(true); return; }
    fileRef.current?.click();
  };

  const uploadFace = async (file) => {
    setFaceErr("");
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setFaceErr("Photo must be under 5 MB."); return; }
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      setFaceErr("Only JPEG, PNG, or WebP images are accepted.");
      return;
    }
    setFaceBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tc_accepted", "true");
      const r = await api.post("/showo/make/face", fd, { headers: { ...auth(), "Content-Type": "multipart/form-data" } });
      setFace(r.data);
    } catch (ex) {
      setFaceErr(ex?.response?.data?.detail || "That photo didn't pass our safety checks. Try a clear, front-facing photo of just yourself.");
    } finally {
      setFaceBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeFace = async () => {
    if (!face?.face_id) return;
    try { await api.delete(`/showo/make/face/${face.face_id}`, { headers: auth() }); } catch (_) {}
    setFace(null);
  };

  const deleteMovie = async (jobId) => {
    if (!jobId) return;
    if (!window.confirm("Delete this movie? This can't be undone.")) return;
    try {
      await api.delete(`/showo/make/${jobId}`, { headers: auth() });
      setHistory(h => h.filter(j => j.job_id !== jobId));
      if (active?.job_id === jobId) setActive(null);
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Could not delete this movie.");
    }
  };

  const saveEdit = async (patch) => {
    const r = await api.patch(`/showo/make/${editing.job_id}`, patch, { headers: auth() });
    setHistory(h => h.map(j => j.job_id === r.data.job_id ? { ...j, ...r.data } : j));
    if (active?.job_id === r.data.job_id) setActive(a => ({ ...a, ...r.data }));
  };

  const status = active?.status || "";
  const stepPct = { queued: 5, scripting: 20, imaging: 45, narrating: 65, rendering: 85, publishing: 95, ready: 100, failed: 100 }[status] || 0;

  return (
    <main className="min-h-screen bg-[#08081a] text-white relative pb-24" data-testid="showo-make">
      <div className="absolute inset-0 pointer-events-none -z-0" aria-hidden>
        <img src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/70 via-[#08081a]/90 to-[#08081a]" />
      </div>

      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/showo" className="flex items-center gap-2" data-testid="showo-make-back">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <Film className="w-5 h-5 text-emerald-400" />
            <span className="fl-display text-lg font-black tracking-tight">Showo <span className="text-emerald-400 text-xs uppercase tracking-widest ml-1">Make</span></span>
          </Link>
          <Link to="/showo" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-make-nav-showo">Back to Showo</Link>
        </div>
      </header>
      <nav className="relative z-10 border-b border-white/5 bg-black/30">
        <div className="max-w-5xl mx-auto px-5 py-2 flex items-center gap-4 text-xs fl-mono uppercase tracking-widest">
          <Link to="/showo/make" className="text-emerald-400" data-testid="sm-nav-make">Make</Link>
          <Link to="/showo/make/about" className="text-white/60 hover:text-emerald-400" data-testid="sm-nav-about">About</Link>
          <Link to="/showo/make/contact" className="text-white/60 hover:text-emerald-400" data-testid="sm-nav-contact">Contact</Link>
          <Link to="/showo/make/admin" className="text-white/60 hover:text-emerald-400 ml-auto" data-testid="sm-nav-admin">Admin</Link>
        </div>
      </nav>

      <section className="relative z-10 max-w-3xl mx-auto px-5 pt-10 pb-6">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-400">// One sentence · One movie · Ninety seconds</div>
        <h1 className="fl-display text-4xl sm:text-5xl font-black mt-2 tracking-tight leading-[1.02]">
          Turn any idea into a<br /><span className="text-emerald-400">short film.</span>
        </h1>
        <p className="text-slate-300 mt-3 max-w-2xl">
          Type one sentence. Pick a genre and a length. We write the script, paint every scene, record the narration, and render a vertical 9:16 movie you can share to Showo, TikTok, or Reels.
        </p>
      </section>

      <section className="relative z-10 max-w-3xl mx-auto px-5">
        <form onSubmit={submit} className="fl-card p-5 bg-white/[0.03] border-white/10 space-y-4" data-testid="showo-make-form">
          <div>
            <label className="fl-mono text-[10px] uppercase tracking-widest text-white/60">// Your one-sentence idea</label>
            <textarea
              className="fl-input mt-1 h-24"
              placeholder="e.g. A retired robot chef enters a cooking contest to save its diner."
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              maxLength={400}
              data-testid="showo-make-prompt"
            />
            <div className="text-[10px] text-white/40 mt-1 flex justify-between">
              <span>Keep it vivid — one clear character, one clear conflict.</span>
              <span>{prompt.length}/400</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="fl-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1"><Wand2 className="w-3 h-3" /> Genre</label>
              <select className="fl-input mt-1" value={genre} onChange={e => setGenre(e.target.value)} data-testid="showo-make-genre">
                {(config?.genres || ["drama"]).map(g => <option key={g} value={g}>{g.replace("-", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="fl-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1"><Clock className="w-3 h-3" /> Length</label>
              <select className="fl-input mt-1" value={length} onChange={e => setLength(e.target.value)} data-testid="showo-make-length">
                {(config?.lengths || [{id:"short",scenes:3,approx_seconds:45},{id:"standard",scenes:6,approx_seconds:90},{id:"long",scenes:10,approx_seconds:150}]).map(l => {
                  const mins = Math.round(l.approx_seconds / 60);
                  const label = l.approx_seconds >= 300
                    ? `${l.id[0].toUpperCase()+l.id.slice(1)} — ${l.scenes} scenes (~${mins} min)`
                    : `${l.id[0].toUpperCase()+l.id.slice(1)} — ${l.scenes} scenes (~${l.approx_seconds}s)`;
                  return <option key={l.id} value={l.id}>{label}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="fl-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1"><Volume2 className="w-3 h-3" /> Narrator voice</label>
              <select className="fl-input mt-1" value={voice} onChange={e => setVoice(e.target.value)} data-testid="showo-make-voice">
                {(config?.voices || ["onyx","alloy","echo","fable","nova","shimmer"]).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="border-t border-white/10 pt-4" data-testid="showo-make-face-section">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5"><User className="w-3 h-3" /> Star in your own movie <span className="text-white/40 normal-case tracking-normal ml-1">— optional</span></div>
                <p className="text-xs text-white/60 mt-1">Upload one clear photo of your face and we'll cast you as the lead across every scene. ~70% likeness in v1.</p>
              </div>
              {face ? (
                <div className="flex items-center gap-2" data-testid="showo-make-face-preview">
                  <img src={face.preview_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-emerald-400" />
                  <button type="button" onClick={removeFace} className="text-white/60 hover:text-red-400" data-testid="showo-make-face-remove" aria-label="Remove face"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onPickFile}
                  disabled={faceBusy}
                  className="fl-btn ghost !py-1.5 !px-3 text-xs shrink-0"
                  data-testid="showo-make-face-upload"
                >
                  {faceBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {faceBusy ? "Checking…" : "Upload photo"}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => uploadFace(e.target.files?.[0])}
                data-testid="showo-make-face-input"
              />
            </div>
            {face && (
              <div className="mt-2 text-[11px] text-emerald-300 flex items-start gap-1.5" data-testid="showo-make-face-desc">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>You're cast: <em className="text-white/70">"{face.description}"</em></span>
              </div>
            )}
            {faceErr && (
              <div className="mt-2 text-red-400 text-xs flex items-start gap-1.5" data-testid="showo-make-face-err">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{faceErr}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none" data-testid="showo-make-publish-wrap">
            <input type="checkbox" checked={publish} onChange={e => setPublish(e.target.checked)} className="accent-emerald-500 w-4 h-4" data-testid="showo-make-publish" />
            <span>Publish to Showo feed when it's ready <span className="text-white/40 text-xs">(uncheck to keep it private — you'll still get the download link)</span></span>
          </label>

          {err && <div className="text-red-400 text-sm flex items-center gap-2" data-testid="showo-make-err"><AlertCircle className="w-4 h-4" />{err}</div>}

          <button
            type="submit"
            disabled={busy || (active && ["queued","scripting","imaging","narrating","rendering","publishing"].includes(active.status))}
            className="fl-btn !bg-emerald-500 !text-black w-full justify-center"
            data-testid="showo-make-submit"
          >
            {busy || (active && active.status !== "ready" && active.status !== "failed" && active.status) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Starting…" : (active && active.status !== "ready" && active.status !== "failed" ? "Rendering…" : "Make my movie")}
          </button>
          <div className="text-[10px] text-white/40 fl-mono uppercase tracking-widest text-center">
            // Admin-only during MVP · ~30-90 seconds render · Zero credits from you
          </div>
        </form>
      </section>

      {active && (
        <section className="relative z-10 max-w-3xl mx-auto px-5 mt-6" data-testid="showo-make-active">
          <div className="fl-card p-5 bg-white/[0.03] border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400">
                {active.status === "ready" ? <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Ready</span> :
                 active.status === "failed" ? <span className="flex items-center gap-1 text-red-400"><AlertCircle className="w-3.5 h-3.5" /> Failed</span> :
                 <span className="flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {STATUS_LABELS[active.status] || active.status}</span>}
              </div>
              <div className="text-[10px] text-white/40 fl-mono">{active.step || ""}</div>
            </div>
            {active.status !== "failed" && (
              <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${stepPct}%` }} />
              </div>
            )}
            {active.title && <div className="fl-display text-xl font-black mt-3">{active.title}</div>}
            {active.status === "ready" && active.video_url && (
              <div className="mt-3 space-y-3">
                <video controls playsInline poster={active.cover_url} className="w-full max-w-sm mx-auto rounded-lg border border-white/10" data-testid="showo-make-video">
                  <source src={active.video_url} type="video/mp4" />
                </video>
                <div className="flex flex-wrap gap-2 justify-center">
                  <a href={active.video_url} download className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-make-download">
                    <Download className="w-3.5 h-3.5" /> Download MP4
                  </a>
                  {active.film_id && (
                    <Link to={`/showo/f/${active.film_id}`} className="fl-btn !bg-emerald-500 !text-black !py-1.5 !px-3 text-xs" data-testid="showo-make-viewpublished">
                      <ExternalLink className="w-3.5 h-3.5" /> View on Showo
                    </Link>
                  )}
                </div>
              </div>
            )}
            {active.status === "failed" && (
              <div className="mt-3 text-red-400 text-sm">{active.error || "Render failed."}</div>
            )}
          </div>
        </section>
      )}

      {history.length > 0 && (
        <section className="relative z-10 max-w-3xl mx-auto px-5 mt-10" data-testid="showo-make-history">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60 mb-3">// Your recent movies</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {history.map(j => (
              <div key={j.job_id} className="fl-card p-2 bg-white/[0.03] border-white/10 relative group" data-testid={`showo-make-hist-${j.job_id}`}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteMovie(j.job_id); }}
                  className="absolute top-1 right-1 z-10 p-1 rounded bg-black/60 text-red-300 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition"
                  title="Delete movie"
                  data-testid={`showo-make-del-${j.job_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="aspect-[9/16] rounded-md overflow-hidden bg-black/50 relative">
                  {j.cover_url ? (
                    <img src={j.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30"><Film className="w-8 h-8" /></div>
                  )}
                  {j.status === "ready" && j.video_url && (
                    <a href={j.video_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition">
                      <Play className="w-8 h-8 text-white" />
                    </a>
                  )}
                </div>
                <div className="mt-2 text-xs text-white truncate font-semibold">{j.title || j.prompt}</div>
                <div className="mt-0.5 text-[10px] fl-mono uppercase tracking-widest text-white/50 truncate">
                  {j.status === "ready" ? `${j.genre} · ${j.scenes_count} scenes` : (STATUS_LABELS[j.status] || j.status)}
                </div>
                {j.status === "ready" && (
                  <div className="mt-2 flex gap-1.5">
                    <button type="button" onClick={() => setEditing(j)} className="fl-btn ghost !py-1 !px-2 text-[11px] flex-1 justify-center min-w-0" data-testid={`showo-make-edit-${j.job_id}`}><Pencil className="w-3 h-3" /> Edit</button>
                    {j.video_url && <DownloadButton path={`/showo/make/${j.job_id}/download`} title={j.title} headers={auth} testId={`showo-make-dl-${j.job_id}`} label="MP4" className="!py-1 !px-2 !text-[11px] flex-1 justify-center min-w-0" />}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {editing && <EditProjectModal project={editing} headers={auth} onSave={saveEdit} onClose={() => setEditing(null)} />}

      {/* T&C modal for face upload — must be accepted BEFORE the file picker opens */}
      {tcOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="showo-make-tc-modal">
          <div className="fl-card p-6 bg-[#0b0c1a] border-emerald-400/40 max-w-md w-full space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <div className="fl-display text-lg font-black">Face-photo terms</div>
            </div>
            <ul className="text-sm text-white/80 space-y-2 list-disc pl-5">
              <li>You confirm the photo is <strong>of you</strong> — or of a person who has given you clear consent to appear in a generated short film.</li>
              <li>You will not upload photos of <strong>minors</strong>, of recognizable <strong>public figures</strong>, or of any real person you do not have permission to depict.</li>
              <li>You will not use Showo Make to create sexual, harassing, defamatory, or deceptive content about any real person.</li>
              <li>Your photo is scanned by an automated moderator; it is <strong>auto-deleted from our servers</strong> as soon as your movie renders (or within 24 hours, whichever comes first). Only a short text description survives, so we can keep the same face across every scene.</li>
              <li>Face likeness in v1 is approximate (~70%) — this is a description-guided render, not a per-frame face swap.</li>
              <li>You accept full responsibility for what you generate. Fliboe may remove content that breaks these terms.</li>
            </ul>
            <label className="flex items-start gap-2 text-sm text-white cursor-pointer select-none" data-testid="showo-make-tc-checkbox-wrap">
              <input
                type="checkbox"
                checked={tcAccepted}
                onChange={e => setTcAccepted(e.target.checked)}
                className="accent-emerald-500 w-4 h-4 mt-0.5"
                data-testid="showo-make-tc-checkbox"
              />
              <span>I have read and I accept these terms.</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setTcOpen(false); setTcAccepted(false); }}
                className="fl-btn ghost !py-1.5 !px-3 text-xs"
                data-testid="showo-make-tc-cancel"
              >Cancel</button>
              <button
                type="button"
                disabled={!tcAccepted}
                onClick={() => { setTcOpen(false); setTimeout(() => fileRef.current?.click(), 60); }}
                className="fl-btn !bg-emerald-500 !text-black !py-1.5 !px-3 text-xs"
                data-testid="showo-make-tc-confirm"
              >Agree &amp; pick photo</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
